import { CONSTANTS } from './constants.js';

// Håndterer alt lager-relateret logik
export class StorageManager {
  constructor(config) {
    // Support both old and new config formats
    const htesCapacity = config.htes_capacity_MWh || (205 * (config.htes_modules || 1));
    const ltesCapacity = config.ltes_capacity_MWh || config.ltes_capacity || 205;
    const batteryCapacity = config.battery_MWh || 3;
    
    // Initial levels from config or defaults
    const initialHTESLevel = config.initial_htes_level !== undefined ? config.initial_htes_level : 0.5;
    const initialLTESLevel = config.initial_ltes_level !== undefined ? config.initial_ltes_level : 0.5;
    const initialBatteryLevel = config.initial_battery_level !== undefined ? config.initial_battery_level : 0;
    
    this.storage = {
      htes: {
        capacity: htesCapacity,
        current: htesCapacity * initialHTESLevel,
        initial: htesCapacity * initialHTESLevel,
        temperature: 350, // °C
        min_reserve: CONSTANTS.STORAGE_RESERVES.htes_min,
        max_level: CONSTANTS.STORAGE_RESERVES.htes_max,
        valueDKK: htesCapacity * initialHTESLevel * CONSTANTS.INITIAL_INVENTORY_PRICE
      },
      ltes: {
        capacity: ltesCapacity,
        current: ltesCapacity * initialLTESLevel,
        initial: ltesCapacity * initialLTESLevel,
        temperature: 95, // °C
        min_reserve: CONSTANTS.STORAGE_RESERVES.ltes_min,
        max_level: CONSTANTS.STORAGE_RESERVES.ltes_max,
        valueDKK: ltesCapacity * initialLTESLevel * CONSTANTS.INITIAL_INVENTORY_PRICE
      },
      battery: {
        capacity: batteryCapacity,
        current: batteryCapacity * initialBatteryLevel,
        initial: batteryCapacity * initialBatteryLevel,
        min_reserve: 0.00,  // 0% for full daily cycles
        max_level: CONSTANTS.STORAGE_RESERVES.battery_max,
        valueDKK: 0
      }
    };
    
    this.efficiency = CONSTANTS.EFFICIENCY;
    this.consumedInitialInventory = {
      htes: 0,
      ltes: 0,
      total: 0,
      valueDKK: 0
    };
  }
  
  // Get current levels as percentages
  getLevels() {
    return {
      htes: this.storage.htes.current / this.storage.htes.capacity,
      ltes: this.storage.ltes.current / this.storage.ltes.capacity,
      battery: this.storage.battery.capacity > 0 
        ? this.storage.battery.current / this.storage.battery.capacity 
        : 0
    };
  }
  
  // Calculate available energy for demand
  getAvailableForDemand() {
    const levels = this.getLevels();
    
    const ltesAvailable = Math.max(0,
      (this.storage.ltes.current - this.storage.ltes.capacity * this.storage.ltes.min_reserve) 
      * this.efficiency.ltes_discharge
    );
    
    const htesAvailable = Math.max(0,
      (this.storage.htes.current - this.storage.htes.capacity * this.storage.htes.min_reserve) 
      * this.efficiency.htes_discharge
    );
    
    // HTES via turbine (max 10MW input, produces 85% heat)
    const maxTurbineInput = Math.min(10, htesAvailable);
    const potentialHeatFromTurbine = maxTurbineInput * this.efficiency.turbine_heat;
    
    return {
      ltes: ltesAvailable,
      htes: htesAvailable,
      turbineInput: maxTurbineInput,
      turbineHeat: potentialHeatFromTurbine,
      total: ltesAvailable + potentialHeatFromTurbine
    };
  }
  
  // Calculate available space for charging
  getAvailableSpace() {
    const levels = this.getLevels();
    
    const ltesSpace = (this.storage.ltes.capacity * this.storage.ltes.max_level - this.storage.ltes.current) 
                     / this.efficiency.ltes_charge;
    
    const htesSpace = (this.storage.htes.capacity * this.storage.htes.max_level - this.storage.htes.current) 
                     / this.efficiency.heater_to_htes;
    
    const batterySpace = this.storage.battery.capacity > 0
      ? (this.storage.battery.capacity * this.storage.battery.max_level - this.storage.battery.current) 
        / Math.sqrt(this.efficiency.battery_roundtrip)
      : 0;
    
    return {
      ltes: Math.max(0, ltesSpace),
      htes: Math.max(0, htesSpace),
      battery: Math.max(0, batterySpace)
    };
  }
  
  // Charge LTES
  chargeLTES(amount, efficiency = this.efficiency.ltes_charge) {
    const stored = amount * efficiency;
    this.storage.ltes.current = Math.min(
      this.storage.ltes.current + stored,
      this.storage.ltes.capacity * this.storage.ltes.max_level
    );
    return stored;
  }
  
  // Charge HTES
  chargeHTES(amount, efficiency = this.efficiency.heater_to_htes) {
    const stored = amount * efficiency;
    this.storage.htes.current = Math.min(
      this.storage.htes.current + stored,
      this.storage.htes.capacity * this.storage.htes.max_level
    );
    return stored;
  }
  
  // Discharge LTES
  dischargeLTES(amount) {
    const needed = amount / this.efficiency.ltes_discharge;
    const available = this.storage.ltes.current - this.storage.ltes.capacity * this.storage.ltes.min_reserve;
    const removed = Math.min(needed, available);
    
    if (removed > 0) {
      this.storage.ltes.current -= removed;
      
      // Track initial inventory consumption
      const consumeInitial = Math.min(removed, this.storage.ltes.initial);
      if (consumeInitial > 0) {
        this.storage.ltes.initial -= consumeInitial;
        const cogs = consumeInitial * CONSTANTS.INITIAL_INVENTORY_PRICE;
        this.consumedInitialInventory.ltes += consumeInitial;
        this.consumedInitialInventory.valueDKK += cogs;
        this.storage.ltes.valueDKK = Math.max(0, this.storage.ltes.valueDKK - cogs);
      }
      
      return removed * this.efficiency.ltes_discharge;
    }
    return 0;
  }
  
  // Discharge HTES
  dischargeHTES(amount) {
    const needed = amount / this.efficiency.htes_discharge;
    const available = this.storage.htes.current - this.storage.htes.capacity * this.storage.htes.min_reserve;
    const removed = Math.min(needed, available);
    
    if (removed > 0) {
      this.storage.htes.current -= removed;
      
      // Track initial inventory consumption
      const consumeInitial = Math.min(removed, this.storage.htes.initial);
      if (consumeInitial > 0) {
        this.storage.htes.initial -= consumeInitial;
        const cogs = consumeInitial * CONSTANTS.INITIAL_INVENTORY_PRICE;
        this.consumedInitialInventory.htes += consumeInitial;
        this.consumedInitialInventory.valueDKK += cogs;
        this.storage.htes.valueDKK = Math.max(0, this.storage.htes.valueDKK - cogs);
      }
      
      return removed * this.efficiency.htes_discharge;
    }
    return 0;
  }
  
  // Battery operations
  chargeBattery(amount) {
    const efficiency = this.efficiency.battery_charge || 0.95; // 95% charge efficiency
    const stored = amount * efficiency;
    this.storage.battery.current = Math.min(
      this.storage.battery.current + stored,
      this.storage.battery.capacity * this.storage.battery.max_level
    );
    return {
      stored: stored,
      loss: amount - stored
    };
  }

  dischargeBattery(amount) {
    const efficiency = this.efficiency.battery_discharge || 0.95; // 95% discharge efficiency
    const available = this.storage.battery.current -
                     this.storage.battery.capacity * this.storage.battery.min_reserve;
    const discharge = Math.min(amount, available);

    if (discharge > 0) {
      this.storage.battery.current -= discharge;
      const output = discharge * efficiency;
      return {
        output: output,
        loss: discharge - output
      };
    }
    return { output: 0, loss: 0 };
  }
  
  // Get total stored energy
  getTotalStoredEnergy() {
    return this.storage.htes.current + 
           this.storage.ltes.current + 
           this.storage.battery.current;
  }
  
  // Get initial storage energy
  getInitialEnergy() {
    return {
      htes: 205 * 2 * 0.5, // Initial 50% of 2 modules
      ltes: 102.5,         // Initial 50% of 1 tank
      battery: 0,
      total: 205 * 2 * 0.5 + 102.5
    };
  }
}

export default StorageManager;